import React, { useState, useEffect } from 'react';
import { TrainingStats, videosAPI } from '../../services/api';
import {
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, CardHeader, Badge, Spinner, StatCard, EmptyState, PageHero } from '../ui';

const TrainingProgress: React.FC = () => {
  const [stats, setStats] = useState<TrainingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const data = await videosAPI.getStats();
      setStats(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch training statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card variant="bordered" className="border-error-200 bg-error-50">
        <CardBody className="p-4">
          <p className="text-sm text-error-600">{error}</p>
        </CardBody>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card variant="glass" hoverable clickable>
        <CardBody>
          <EmptyState
            title="No training data"
            description="No training data available yet."
            icon={<AcademicCapIcon className="h-12 w-12" />}
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Enhanced Hero Section */}
      <PageHero
        title="Training Progress Overview"
        subtitle="Monitor salesmen training completion status and progress"
        icon={<AcademicCapIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="pink"
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Salesmen"
          value={stats.totalUsers}
          icon={<UserIcon className="h-6 w-6" />}
          variant="primary"
        />
        <StatCard
          title="Training Completed"
          value={stats.completedUsers}
          icon={<CheckCircleIcon className="h-6 w-6" />}
          variant="success"
        />
        <StatCard
          title="In Progress"
          value={stats.totalUsers - stats.completedUsers}
          icon={<ClockIcon className="h-6 w-6" />}
          variant="warning"
        />
        <StatCard
          title="Completion Rate"
          value={`${stats.completionRate.toFixed(1)}%`}
          icon={<AcademicCapIcon className="h-6 w-6" />}
          variant="secondary"
        />
      </div>

      {/* Progress Bar */}
      <Card variant="glass" hoverable clickable>
        <CardHeader title="Overall Progress" />
        <CardBody>
          <div className="w-full bg-neutral-200 rounded-full h-4">
            <div
              className="bg-primary-600 h-4 rounded-full transition-all duration-300"
              style={{ width: `${stats.completionRate}%` }}
            ></div>
          </div>
          <p className="mt-2 text-sm text-neutral-600">
            {stats.completedUsers} of {stats.totalUsers} salesmen have completed their training
          </p>
        </CardBody>
      </Card>

      {/* Individual User Progress */}
      <Card variant="glass" hoverable clickable>
        <CardHeader title="Individual Progress" />
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Salesman
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Completed Videos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {stats.userProgress.map((user) => {
                  const progressPercentage = stats.totalVideos > 0
                    ? (user.completedRequiredVideos / stats.totalVideos) * 100
                    : 0;

                  return (
                    <tr key={user.id} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-primary-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-neutral-900">
                              {user.firstName && user.lastName
                                ? `${user.firstName} ${user.lastName}`
                                : user.email}
                            </div>
                            <div className="text-sm text-neutral-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-32 bg-neutral-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${user.trainingCompleted ? 'bg-success-500' : 'bg-warning-500'
                              }`}
                            style={{ width: `${progressPercentage}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-neutral-500 mt-1">
                          {progressPercentage.toFixed(0)}%
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        <Badge variant="neutral">
                          {user.completedRequiredVideos} / {stats.totalVideos}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={user.trainingCompleted ? 'success' : 'warning'}>
                          {user.trainingCompleted ? (
                            <>
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Completed
                            </>
                          ) : (
                            <>
                              <ClockIcon className="h-3 w-3 mr-1" />
                              In Progress
                            </>
                          )}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default TrainingProgress;
